
import { useRef, useCallback, startTransition } from 'react';
import { apiClient } from '@/lib/apiClient';

export const useTwilioCallStatus = (
  callStatus: string,
  setCallStatus: (status: string) => void,
  endCallState: () => void
) => {
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);
  const currentCallSidRef = useRef<string | null>(null);

  // Poll call status from backend
  const pollCallStatus = useCallback(async (callSid: string) => {
    // Only poll if this is still the current call
    if (currentCallSidRef.current !== callSid) {
      console.log(`🚫 Skipping poll for old call ${callSid}, current: ${currentCallSidRef.current}`);
      return;
    }

    try {
      const { status } = await apiClient.get(`/twilio/call-status/${callSid}`);
      console.log(`🔄 Polled status for ${callSid}: ${status}`);
      
      // Double check we're still polling the right call
      if (currentCallSidRef.current !== callSid) {
        console.log(`🚫 Call SID changed during polling, ignoring result for ${callSid}`);
        return;
      }
      
      startTransition(() => {
        // Update UI status based on backend status with priority for ringing
        if (status === 'ringing' && callStatus !== 'ringing') {
          console.log('📞 Call is ringing, updating UI (priority)');
          setCallStatus('ringing');
        } else if (status === 'in-progress' && callStatus !== 'open' && callStatus !== 'ringing') {
          console.log('✅ Call is in-progress, updating UI to connected');
          setCallStatus('open');
        } else if (status === 'connecting' && callStatus !== 'connecting' && callStatus !== 'ringing') {
          console.log('🔄 Call is connecting, updating UI');
          setCallStatus('connecting');
        } else if (['completed', 'failed', 'busy', 'no-answer', 'cancelled'].includes(status)) {
          console.log(`📴 Call ended with status: ${status}, stopping polling and ending call state`);
          stopStatusPolling();
          endCallState();
        }
      });
    } catch (error) {
      console.error('❌ Error polling call status:', error);
    }
  }, [callStatus, setCallStatus, endCallState]);

  // Start status polling
  const startStatusPolling = useCallback((callSid: string) => {
    console.log(`🔄 Starting status polling for call: ${callSid}`);
    
    // Stop any existing polling first
    stopStatusPolling();
    
    // Set the current call SID
    currentCallSidRef.current = callSid;
    
    statusPollingRef.current = setInterval(() => {
      pollCallStatus(callSid);
    }, 2000);
  }, [pollCallStatus]);

  // Stop status polling
  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      console.log('🛑 Stopping status polling');
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
    // Clear the current call SID when stopping
    currentCallSidRef.current = null;
  }, []);

  return {
    startStatusPolling,
    stopStatusPolling
  };
};
